"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTeammates } from "../model/use-teammates";
interface CallInvitePickerProps {
  /** Current roster user ids + self — already on the call, not invitable. */
  readonly excludeUserIds: ReadonlySet<string>;
  readonly onPick: (userId: string) => void;
  readonly onClose: () => void;
}

/**
 * Teammate picker for pulling someone into a live call (call-parity §11.3) —
 * the dark-stage sibling of the group AddMemberPicker: same candidate source
 * (useTeammates), same exclude-set + query filtering, same outside-click/Escape
 * dismissal, styled for the call stage's dark surface.
 */
export function CallInvitePicker(t0) {
  const $ = _c(25);
  const {
    excludeUserIds,
    onPick,
    onClose
  } = t0;
  const {
    data: teammates,
    isLoading
  } = useTeammates();
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== onClose) {
    t1 = () => {
      const onPointer = e => {
        if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
          onClose();
        }
      };
      const onKey = e_0 => {
        if (e_0.key === "Escape") {
          onClose();
        }
      };
      document.addEventListener("pointerdown", onPointer);
      document.addEventListener("keydown", onKey);
      return () => {
        document.removeEventListener("pointerdown", onPointer);
        document.removeEventListener("keydown", onKey);
      };
    };
    t2 = [onClose];
    $[0] = onClose;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== excludeUserIds || $[4] !== query || $[5] !== teammates) {
    const q = query.trim().toLowerCase();
    t3 = (teammates ?? []).filter(t => !excludeUserIds.has(t.userId) && t.name.toLowerCase().includes(q));
    $[3] = excludeUserIds;
    $[4] = query;
    $[5] = teammates;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const candidates = t3;
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Search className="h-3.5 w-3.5 shrink-0 text-white/40" aria-hidden={true} />;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_1 => setQuery(e_1.target.value);
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== query) {
    t6 = <input autoFocus={true} value={query} onChange={t5} placeholder={"Add someone to the call\u2026"} aria-label="Search teammates to add to the call" className="w-full bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-hidden" />;
    $[9] = query;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== onClose) {
    t7 = <button type="button" onClick={onClose} aria-label="Close" className="text-xs text-white/40 hover:text-white/70">Esc</button>;
    $[11] = onClose;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6 || $[14] !== t7) {
    t8 = <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">{t4}{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== candidates || $[17] !== isLoading || $[18] !== onPick) {
    t9 = isLoading ? <li className="px-3 py-2 text-xs text-white/40">Loading teammates…</li> : candidates.length === 0 ? <li className="px-3 py-2 text-xs text-white/40">No teammates to add.</li> : candidates.map(t_0 => <li key={t_0.userId}><button type="button" onClick={() => onPick(t_0.userId)} className={cn("flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors", "hover:bg-white/8 focus:outline-hidden focus-visible:bg-white/8")}><span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#5651ff]/30 text-xs font-semibold text-[#c8c5ff]">{t_0.imageUrl ? <img src={t_0.imageUrl} alt="" className="h-full w-full object-cover" /> : t_0.name.charAt(0).toUpperCase()}</span><span className="min-w-0 flex-1"><span className="block truncate text-sm text-white">{t_0.name}</span>{t_0.email ? <span className="block truncate text-[11px] text-white/40">{t_0.email}</span> : null}</span></button></li>);
    $[16] = candidates;
    $[17] = isLoading;
    $[18] = onPick;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== t9) {
    t10 = <ul className="max-h-56 overflow-y-auto py-1">{t9}</ul>;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  let t11;
  if ($[22] !== t10 || $[23] !== t8) {
    t11 = <div ref={rootRef} data-testid="call-invite-picker" className="w-72 overflow-hidden rounded-xl bg-[#141a30] shadow-2xl ring-1 ring-white/15">{t8}{t10}</div>;
    $[22] = t10;
    $[23] = t8;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}